/**
 * ClientRecord — the Kith client record page (PR-C1 shell; PR-C2 wired
 * the LIVE aggregate read).
 *
 * A gated clinical surface: the page opens on read_clinical_record, the
 * same floor the API handler gates on (GET .../record). Holding that
 * capability opens the SURFACE; opening a given client still requires
 * that the caller's reach includes it (her service's lane, her care
 * teams, or every client under view_all_clinical or the tenant's
 * Everyone on staff setting). Otherwise the server's
 * assertViewerMaySeeClient gate refuses with 403 not_on_care_team,
 * rendered as its own calm denied state below, which wins over a record
 * still cached from before the refusal (distinct from the role-denial
 * state PageShell's `access` prop renders for a viewer without
 * read_clinical_record at all). Access
 * is consumed as a PROP inside the shell, so the early-return sits BELOW
 * all hooks (the locked UX-8 contract; React #310 trap — see CLAUDE.md +
 * the AppShell #310 memory).
 *
 * Route: /t/:tenantSlug/clients/:clientId (DETAIL_ROUTES, section 'clients').
 * The legacy /clients/:clientId/notes redirects here with ?tab=notes.
 * TS-5: header actions carry Archive/Restore (soft-archive; change_log
 * trail server-side per migration 0122) behind a confirm dialog.
 *
 * Structure (§4):
 *   - vitals band composed ON EntityHeader (§4.2); [New note (N)] in actions;
 *   - a URL-?tab=-driven tab shell with per-tab visibility mirroring the
 *     server gates + firstVisibleTab coercion (§4.3);
 *   - PR-C2: the page fetches the ClientRecordSnapshot ONCE
 *     (useClientRecord → GET /clients/{id}/record) and passes it down —
 *     Overview + Care record + People render the SAME payload (one read =
 *     one phi_access_log row server-side; NO per-tab fetches). The
 *     committed fixture stays in src/lib/__fixtures__/clientRecord.ts as
 *     the contract artifact + test substrate; the page no longer imports it.
 *   - Notes & activity keeps its own live feed; Pre-billing is LIVE with
 *     its own month-scoped read (native billing v0); Documents stays
 *     frame + placeholder.
 *   - loading/error ride AsyncBoundary (house pattern); a denied role
 *     fires NO fetch (the query is enabled only with the capability).
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useSearchParams } from 'react-router-dom';
import { MoreVertical } from 'lucide-react';
import { useTenant } from '@/lib/auth';
import { useSupportContact } from '@/hooks/useSupportContact';
import { contactSentence, pageWriteText } from '@/lib/errorCopy';
import { archiveBody, restoreBody } from '@/lib/recordFormsCopy';
import { ApiError } from '@/lib/api';
import {
  canEditClientDetails,
  canManageClientIdentity,
  canReadClinicalRecord,
  canViewAllClinical,
  canViewInvoice,
  canViewPrebilling,
  canWriteClinicalNotes,
  canWriteDemoClinical,
} from '@/lib/capabilities';
import { useClientRecord } from '@/lib/clientRecordApi';
import { notesApi, type ProposalsResponse } from '@/lib/notes';
import { clientPhotoUrl } from '@/lib/clientsApi';
import { soonestUpcomingAppointment } from '@/lib/upcomingAppointments';
import { lifecycleBadge, nextOccurrence, recurrenceTagLabel } from '@/lib/clientRecord';
import { displayValue } from '@/lib/valueCase';
import { fmtTime, MONTHS, parseIsoDate, rowDate } from '@/lib/proseDate';
import { nextSpiceSession, type SpiceNextSessionResult } from '@/lib/spiceNextSession';
import type { SpiceSessionFrequency, SpiceSessionKind } from '@/lib/spiceVisitPlansApi';
import { effectiveHold } from '@/lib/spiceHold';
import { practiceTodayIso } from '@/lib/practiceDate';
import {
  SPICE_DAY_ABBR,
  SPICE_FREQUENCY_TITLES,
  SPICE_KIND_LABELS,
  SPICE_NO_SCHEDULE_TEXT,
  formatSpiceAway,
  formatSpiceTime,
} from '@/lib/spiceScheduleCopy';
import { TodayContext, useToday } from '@/lib/today';
import { useSetClientArchived } from '@/lib/clientsApi';
import { usePrebilling } from '@/lib/prebilling';
import {
  recordFaceFor,
  resolveRecordServiceSide,
  writeRecordServiceSide,
  type RecordServiceSide,
} from '@/lib/recordServiceSide';
import type { ClientRecordSnapshot } from '@/lib/clientRecord';
import { clientCallName } from '@/lib/clientCallName';
import { PageShell } from '@/components/primitives/PageShell';
import { BrandWatermark } from '@/components/primitives/BrandWatermark';
import { EntityHeader } from '@/components/primitives/EntityHeader';
import { BackToClients } from '@/components/BackToClients';
import { BackControl } from '@/components/BackControl';
import { AsyncBoundary } from '@/components/primitives/AsyncBoundary';
import { Button } from '@/components/primitives/Button';
import { Chip } from '@/components/primitives/Chip';
import { Dialog } from '@/components/primitives/Dialog';
import { ChangeServiceDialog } from '@/components/clientRecord/ChangeServiceDialog';
import {
  ClientNameDialog,
  ENGAGEMENT_LABELS,
  LifecycleStatusDialog,
  PronounsDialog,
  VitalsBand,
} from '@/components/clientRecord/VitalsBand';
import { ClientPhotoDialog } from '@/components/clientRecord/ClientPhotoDialog';
import { FaceSheetDialog } from '@/components/clientRecord/FaceSheetDialog';
import { QrfDialog } from '@/components/clientRecord/QrfDialog';
import { CriticalStrip } from '@/components/clientRecord/CriticalStrip';
import {
  ClientRecordTabs,
  resolveClientTab,
  isClientTabHidden,
  firstVisibleClientTab,
  type ClientRecordTab,
  type ClientTabVisibility,
} from '@/components/clientRecord/ClientRecordTabs';
import {
  CareRecordTab,
  LifeLogisticsTab,
  NotesActivityTab,
  PeopleTab,
  DocumentsTab,
  PreBillingTab,
} from '@/components/clientRecord/tabs';
import { SpiceTab } from '@/components/clientRecord/SpiceTab';
import { RecordJumpStrip } from '@/components/clientRecord/RecordJumpStrip';

/** The one slot every chip on the header band sits in: centred in the
 *  name's row on its own height, never on the row's line height, so a
 *  chip that is a control and one that is not land on one line. */
const HEADER_CHIP_SLOT = 'inline-flex min-w-0 max-w-full items-center self-center leading-none';

// R1: the header's SPICE half of "next appointment": the session's kind,
// weekday + short date + time + therapist + frequency, always led by the
// kind ("SPICE ·", "Friendly Visitor ·") so it never reads as a clinical
// provider's name.

/** 'YYYY-MM-DD' → '9 Sep' (day + short month, no year — the header's
 *  compact register; proseDate's full form carries a year it has no
 *  room for here). */
function shortMonthDay(iso: string): string {
  const d = parseIsoDate(iso);
  return `${d.getDate()} ${MONTHS[d.getMonth()]!.slice(0, 3)}`;
}

/** 'YYYY-MM-DD' (+ 'HH:MM') → 'Thu 29 Oct' / 'Thu 29 Oct · 2:30 PM',
 *  the header's compact register, as the SPICE session reads. */
function shortWhen(iso: string, time?: string | null): string {
  const day = parseIsoDate(iso).getDay();
  const date = `${SPICE_DAY_ABBR[day === 0 ? 7 : day] ?? ''} ${shortMonthDay(iso)}`;
  return time ? `${date} · ${fmtTime(time)}` : date;
}

/**
 * The header's next appointment in two parts (LP-40): `lead` carries the
 * date (or, with none, the state) and is never cut off; `rest` (who, and
 * how often) follows it and is the only part that may shorten. Either may
 * be null: no who, or no date to lead with.
 */
interface NextFact {
  lead: string | null;
  rest: string | null;
}

/** The fact as one line of words (the value's title). */
function nextFactText(f: NextFact): string {
  return [f.lead, f.rest].filter((p): p is string => p !== null).join(' · ');
}

/** "Tue 9 Sep · 2:00 PM · SPICE · Toni Salvato · Weekly": the date leads
 *  (LP-40), and the kind still comes before the therapist (R1), so the
 *  session never reads as a clinical provider's name. */
function spiceSessionLabel(
  result: Extract<SpiceNextSessionResult, { kind: 'session' }>,
  therapistName: string,
  session: { kind: SpiceSessionKind; frequency: SpiceSessionFrequency } | undefined,
): NextFact {
  const weekday = SPICE_DAY_ABBR[result.weekday] ?? '';
  const kind = SPICE_KIND_LABELS[session?.kind ?? 'spice'];
  const frequency = SPICE_FREQUENCY_TITLES[session?.frequency ?? 'weekly'];
  return {
    lead: `${weekday} ${shortMonthDay(result.date)} · ${formatSpiceTime(result.startTime)}`,
    rest: `${kind} · ${therapistName} · ${frequency}`,
  };
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

/**
 * A5 (Design Pass 2): the hero's dead right side now carries the two
 * facts a CM most wants on arrival — the next appointment (from the ONE
 * aggregate read, the OverviewRail derivation) and this month's billing
 * state (the SAME usePrebilling read + query key the Pre-billing tab
 * makes, gated like the tab: view_invoice or view_prebilling; opening
 * the tab reuses the cache).
 *
 * S3.1 (launch-UX, founder-ruled): the facts no longer vanish below md —
 * ONE component, responsive: a compact full-width row inside the band on
 * phones (the parent flex wraps), the same right-side column at md+.
 * Same testids, same data, same gating on every width.
 */
function HeroFacts({
  record,
  today,
  canSeePrebilling,
}: {
  record: ClientRecordSnapshot;
  today: string;
  canSeePrebilling: boolean;
}): ReactNode {
  const { tenantSlug = '', clientId = '' } = useParams<{ tenantSlug: string; clientId: string }>();
  const month = today.slice(0, 7);
  const { rows, isLoading: billingLoading } = usePrebilling(tenantSlug, clientId, month, {
    enabled: canSeePrebilling,
  });
  // A care manager's planned visit (0240) is the next appointment too:
  // the header is the record's first fact, whichever tab the record opens
  // on. It rides the one record read; one already behind the viewer's day
  // never shows.
  const plannedVisit = record.vitals.nextCareVisit ?? null;
  const nextCareVisit = plannedVisit !== null && plannedVisit.date >= today ? plannedVisit : null;

  // G1 (Soak #2 #19): the soonest UPCOMING appointment via the shared
  // helper — active + scheduled, stale-past rows excluded, but NO upper
  // window bound so a known future appointment (even beyond a month, or a
  // dateText-only one) still shows instead of a false "None scheduled".
  const next = soonestUpcomingAppointment(record.appointments, today);
  // G2 (Soak #2 Wave B): show the NEXT occurrence (a recurring series never
  // shows its stale anchor) + the cadence tag (the founder's "show only the
  // next recurring and indicate recurring status").
  // nextWhen is the ISO date the SPICE comparison below reads; the label
  // reads it in words, with its time (0218).
  // LP-40 (owner ruling 2026-10-27): the date leads ("Thu 29 Oct ·
  // 2:30 PM · Dr. Lio Marsh") and is never cut off; the provider follows.
  // The provider has no separate credentials field, so its display name
  // stays whole here and only it may shorten.
  const nextWhen = next ? nextOccurrence(next, today) : undefined;
  const nextCadence = next ? recurrenceTagLabel(next.recurrence) : null;
  const nextWho = next ? `${next.provider}${nextCadence !== null ? ` · ${nextCadence}` : ''}` : '';
  const nextLead =
    next === null
      ? null
      : nextWhen !== undefined
        ? shortWhen(nextWhen, next.scheduledTime)
        : next.dateText
          ? displayValue(next.dateText)
          : null;
  const appointmentLabel: NextFact | null =
    next === null ? null : { lead: nextLead, rest: nextWho };
  // The nearer of the appointment and the planned visit; an appointment
  // with no structured date never out-ranks a dated visit. Same day: the
  // earlier time, an untimed one after a timed one.
  const at = (date: string, time: string | null | undefined): string => `${date}T${time ?? '~'}`;
  const visitFirst =
    nextCareVisit !== null &&
    (nextWhen === undefined || at(nextCareVisit.date, nextCareVisit.time) < at(nextWhen, next?.scheduledTime));
  const clinicalLabel: NextFact | null = visitFirst
    ? { lead: shortWhen(nextCareVisit.date, nextCareVisit.time), rest: `Visit · ${nextCareVisit.careManagerName}` }
    : appointmentLabel;
  const clinicalWhen = visitFirst ? nextCareVisit.date : nextWhen;

  // R1: the visit plan's next planned SPICE session, folded into the SAME
  // fact — a client on SPICE (or both services) reads it here, comparing
  // against a clinical appointment on file when there is one and showing
  // whichever is nearer, labeled so the two never read as the same thing.
  // Covered or paused is decided on the practice day, the same day as the
  // page's `today` (outside a test's injected day).
  const schedule = record.vitals.spiceSchedule;
  const state = schedule
    ? effectiveHold(
        schedule.status,
        schedule.holdReason,
        schedule.statusUntil,
        { coverRosterId: schedule.coverRosterId },
        practiceTodayIso(),
      )
    : null;
  const spiceResult = nextSpiceSession(
    schedule && state
      ? {
          status: state.status,
          holdReason: state.hold_reason,
          statusUntil: schedule.statusUntil,
          sessions: schedule.sessions,
        }
      : null,
    today,
  );
  const therapistName = record.vitals.spiceTherapist?.displayName ?? '';

  let nextLabel: NextFact;
  if (spiceResult.kind === 'session') {
    const session = schedule?.sessions.find(
      (s) =>
        s.weekday === spiceResult.weekday &&
        s.startTime === spiceResult.startTime &&
        s.weekOfMonth === spiceResult.weekOfMonth,
    );
    const spiceLabel = spiceSessionLabel(spiceResult, therapistName, session);
    // Whichever is NEARER wins; a clinical appointment with no comparable
    // structured date (dateText-only, or none on file) never out-ranks a
    // concrete SPICE session date.
    nextLabel = clinicalWhen !== undefined && clinicalWhen < spiceResult.date ? clinicalLabel! : spiceLabel;
  } else if (spiceResult.kind === 'status') {
    // No SPICE date to compare: a clinical appointment on file still
    // reads as the next appointment; only when there is none does the
    // cover or the pause itself become the fact.
    nextLabel = clinicalLabel ?? {
      lead: `SPICE · ${formatSpiceAway({
        state: spiceResult.status,
        hold_reason: spiceResult.holdReason,
        hold_note: schedule?.holdNote ?? null,
        status_until: spiceResult.statusUntil,
        cover_name: schedule?.coverName ?? null,
      })}`,
      rest: null,
    };
  } else if (schedule !== null && schedule.sessions.length === 0) {
    nextLabel = clinicalLabel ?? { lead: `SPICE · ${SPICE_NO_SCHEDULE_TEXT}`, rest: null };
  } else {
    nextLabel = clinicalLabel ?? { lead: 'None scheduled', rest: null };
  }

  let billingLabel: string | null = null;
  if (canSeePrebilling && rows !== null) {
    const ready = rows.filter((r) => r.readiness === 'ready').length;
    const awaiting = rows.filter((r) => r.readiness === 'needs_your_ok').length;
    if (ready === 0 && awaiting === 0) {
      billingLabel = 'Nothing to bill yet';
    } else {
      const parts: string[] = [];
      if (ready > 0) parts.push(`${ready} ready`);
      if (awaiting > 0) parts.push(`${awaiting} ${awaiting === 1 ? 'note' : 'notes'} awaiting an OK`);
      billingLabel = parts.join(' · ');
    }
  }

  // Each fact is held to the column's width (max-w-full): in the md+
  // right-aligned column a fact otherwise sizes to its text, and a long
  // appointment ran left over the header's service pill instead of
  // truncating. The full text stays in the value's title.
  const factCls = 'flex flex-col gap-0.5 min-w-0 max-w-full';
  // The ONE label geometry (ratified scale) — warm ink on the forest ground.
  const labelCls = 'text-label tracking-label font-semibold uppercase text-warm-400';
  const valueCls = 'text-value font-medium text-cream/90 truncate';
  return (
    <div
      data-testid="hero-facts"
      className={
        // Mobile: a wrapping fact row spanning the band (parent flex-wrap
        // drops it below the header). md+: the original right-side column.
        'flex w-full flex-row flex-wrap items-start gap-x-6 gap-y-1.5 ' +
        'md:w-auto md:max-w-[260px] md:shrink-0 md:flex-col md:flex-nowrap md:gap-2.5 md:self-center md:items-end md:text-right'
      }
    >
      <div className={factCls}>
        <span className={labelCls}>Next appointment</span>
        {/* LP-40: the date leads and is never cut off. The who follows as
            one inline block: where it does not fit beside the date it
            wraps to a second line whole, and only there, past the width
            of the line, does it shorten. The full words stay in the title. */}
        <span
          className="max-w-full text-value font-medium text-cream/90 [overflow-wrap:anywhere]"
          data-testid="hero-next-appointment"
          title={nextFactText(nextLabel)}
        >
          {nextLabel.lead !== null && <span data-testid="hero-next-appointment-when">{nextLabel.lead}</span>}
          {nextLabel.rest !== null && (
            <>
              {nextLabel.lead !== null && ' · '}
              <span
                data-testid="hero-next-appointment-who"
                className="inline-block max-w-full truncate align-bottom"
              >
                {nextLabel.rest}
              </span>
            </>
          )}
        </span>
      </div>
      {billingLabel !== null ? (
        <div className={factCls}>
          <span className={labelCls}>This month&rsquo;s billing</span>
          <span className={valueCls} data-testid="hero-billing-state">
            {billingLabel}
          </span>
        </div>
      ) : (
        // While the month's rows are on their way the fact holds its
        // place: the label and an empty value line of the same height, so
        // the band does not grow and push the page down when they land. A
        // failed read still hides the fact (isLoading is false once the
        // read has failed).
        canSeePrebilling &&
        billingLoading && (
          <div className={factCls} data-testid="hero-billing-pending">
            <span className={labelCls}>This month&rsquo;s billing</span>
            <span className={valueCls} aria-hidden="true">
              {'\u00a0'}
            </span>
          </div>
        )
      )}
    </div>
  );
}

/**
 * The denied seat's sentence. Mounted only on the denied screen, so its
 * tenant-config read (cache only: nothing is fetched for a person the
 * page turns away) never rides the record page's own loads.
 */
function RecordDeniedLine(): ReactNode {
  const contact = useSupportContact({ enabled: false });
  return contactSentence(
    "Opening client records isn't part of your access yet. Ask {contact_admin} if your work needs it.",
    contact,
  );
}

/**
 * The archive or restore confirm: names the client with the recorded
 * pronouns, and the primary names the act. A failure reads the house save
 * sentence. Mounted only while open, so its tenant-config read for the
 * support contact never rides the record page's own loads.
 */
function ArchiveConfirmDialog({
  action,
  displayName,
  pronouns,
  pending,
  error,
  onConfirm,
  onClose,
}: {
  action: 'archive' | 'restore';
  displayName: string;
  pronouns: ClientRecordSnapshot['vitals']['pronouns'];
  pending: boolean;
  error: unknown;
  onConfirm: () => void;
  onClose: () => void;
}): ReactNode {
  const contact = useSupportContact();
  const archiving = action === 'archive';
  return (
    <Dialog
      title={archiving ? 'Archive this client?' : 'Restore this client?'}
      role="alertdialog"
      size="sm"
      testId="archive-confirm-dialog"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant={archiving ? 'danger' : 'primary'}
            size="sm"
            loading={pending}
            data-testid="archive-confirm-submit"
            onClick={onConfirm}
          >
            {archiving ? 'Archive client' : 'Restore client'}
          </Button>
        </>
      }
    >
      <p className="text-sm text-ink-soft">
        {archiving ? archiveBody(displayName, pronouns) : restoreBody(displayName)}
      </p>
      {error !== null && (
        <p role="alert" className="mt-2 text-sm text-pepper">
          {pageWriteText(error, contact)}
        </p>
      )}
    </Dialog>
  );
}

/**
 * The record's quiet overflow (kebab) menu on the hero: Print Face Sheet,
 * Change service, Edit name, and Archive client or Restore client.
 * Destructive actions never sit beside the primary action, so the
 * archive/restore lifecycle lives here, with its confirm dialog.
 *
 * Change service shows to a seat that edits a client's everyday details
 * (`canEditDetails`, the engagement route's gate). Edit name, Archive
 * client and Restore client show to a manage_client_identity holder
 * (`canEdit`), the capability the archive, restore and name routes
 * require. "Change status" is NOT in this menu: the header status chip is
 * its own door.
 */
function HeroOverflowMenu({
  isActive,
  canEdit,
  canEditDetails,
  onArchive,
  onRestore,
  onFaceSheet,
  onQrf,
  onChangeService,
  onRename,
}: {
  isActive: boolean;
  /** manage_client_identity: gates Edit name, and Archive client or Restore client. */
  canEdit: boolean;
  /** canEditClientDetails: gates Change service. */
  canEditDetails: boolean;
  onArchive: () => void;
  onRestore: () => void;
  onFaceSheet: () => void;
  onQrf: () => void;
  onChangeService: () => void;
  onRename: () => void;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent): void {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <Button
        variant="ghost-on-dark"
        size="sm"
        aria-label="More actions"
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="hero-overflow"
        icon={<MoreVertical size={16} aria-hidden="true" />}
        onClick={() => setOpen((v) => !v)}
      />
      {open && (
        <div
          role="menu"
          aria-label="More actions"
          className="absolute right-0 top-full z-40 mt-1 min-w-[180px] rounded-card border border-sage-800/[8%] bg-paper py-1.5 shadow-sh3"
        >
          {/* WS-4 D5: the Face Sheet — the Emergency Folder one-pager.
              Quiet (a read, not a lifecycle flip); gated only by seeing
              the record, exactly like the rest of this menu. */}
          <Button
            variant="ghost"
            size="sm"
            role="menuitem"
            className="w-full !justify-start !rounded-none px-3.5"
            data-testid="face-sheet-action"
            onClick={() => {
              setOpen(false);
              onFaceSheet();
            }}
          >
            Print Face Sheet
          </Button>
          {/* The Client history (LP-33, the Quick Reference's new name): the
              record over time, its own document (the Face Sheet is the
              client as of today). The same gate as the Face Sheet: seeing
              the record. The test id keeps its old name. */}
          <Button
            variant="ghost"
            size="sm"
            role="menuitem"
            className="w-full !justify-start !rounded-none px-3.5"
            data-testid="qrf-action"
            onClick={() => {
              setOpen(false);
              onQrf();
            }}
          >
            Print client history
          </Button>
          {/* Change service: the client's service (Care management, SPICE,
              both) and its leads, the care manager and the SPICE
              therapist, in one dialog. */}
          {canEditDetails && (
            <Button
              variant="ghost"
              size="sm"
              role="menuitem"
              className="w-full !justify-start !rounded-none px-3.5"
              data-testid="header-change-service"
              onClick={() => {
                setOpen(false);
                onChangeService();
              }}
            >
              Change service
            </Button>
          )}
          {/* Edit name: first, last and preferred name. "Change status"
              lives on the header chip. */}
          {canEdit && (
            <Button
              variant="ghost"
              size="sm"
              role="menuitem"
              className="w-full !justify-start !rounded-none px-3.5"
              data-testid="header-rename"
              onClick={() => {
                setOpen(false);
                onRename();
              }}
            >
              Edit name
            </Button>
          )}
          {canEdit && (isActive ? (
            <Button
              variant="ghost"
              size="sm"
              role="menuitem"
              className="w-full !justify-start !rounded-none px-3.5 text-pepper-deep hover:!bg-pepper-soft/50"
              data-testid="archive-client-action"
              onClick={() => {
                setOpen(false);
                onArchive();
              }}
            >
              Archive client
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              role="menuitem"
              className="w-full !justify-start !rounded-none px-3.5"
              data-testid="restore-client-action"
              onClick={() => {
                setOpen(false);
                onRestore();
              }}
            >
              Restore client
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}

export function ClientRecord(): ReactNode {
  const { tenantSlug = '', clientId = '' } = useParams<{ tenantSlug: string; clientId: string }>();
  const { whoami } = useTenant();
  const [searchParams, setSearchParams] = useSearchParams();

  // --- Caps → tab visibility (mirrors the server gates; single source of
  // truth with the active-tab coercion below). All hooks/derivations run
  // BEFORE the gate is consumed inside PageShell (React #310 discipline).
  const canReadClinical = canReadClinicalRecord(whoami);
  // (posting is gated inside the clinical surface; the notes TAB itself needs
  // only clinical read, which every role past the page gate holds)
  // Pre-billing opens on either capability: view_invoice for the office's
  // full screen, view_prebilling for the case-note lines with no money.
  const canSeePrebilling = canViewInvoice(whoami) || canViewPrebilling(whoami);
  // The page opens on read_clinical_record — the same floor the API
  // handler gates on. Care-team RLS decides which clients read back with
  // content versus empty projections; that scoping happens server-side,
  // not at this gate.
  const canOpenRecord = canReadClinicalRecord(whoami);
  // The page opens on read_clinical_record (read), but every
  // client-identity WRITE affordance (Rename, Change status, pronouns, the
  // photo, the Assign/Change CM strip, the About/Contact edits) gates on
  // manage_client_identity — which a read-only viewer does NOT hold, so it
  // sees clean read-only pages.
  const canManageIdentity = canManageClientIdentity(whoami);
  const canEditDetails = canEditClientDetails(whoami);
  // The clinical-content write gate (0152): the header "+ Case Note" CTA is a
  // write-intent affordance (it opens the note composer) — hidden for a
  // read-only viewer, who reaches the read-only Notes feed via the tab bar.
  // Board demo sandbox (0156): a demo-only author (board_observer) gets it too,
  // but ONLY on a demo client — resolved below where the record (vitals.isDemo)
  // is loaded (canAuthorClinicalHere). The server fences the write regardless.
  const canWriteClinical = canWriteClinicalNotes(whoami);
  const canWriteDemo = canWriteDemoClinical(whoami);
  // R5: a viewer who does SPICE, not care management, and holds no
  // whole-practice clinical gate is SPICE-led: a client on both services
  // leads its tab order with SPICE for her. She still reads Care record
  // and Life & logistics; their write doors gate on
  // canWriteClinicalRecord, which is false for her.
  const viewerWritesClinicalRecord = whoami?.does_care_management === true || canViewAllClinical(whoami);
  const spiceLedViewer = whoami?.does_spice === true && !viewerWritesClinicalRecord;

  // --- The record-service toggle: a client on BOTH services shows a
  // Care management/SPICE face, chosen here so the header's note CTA, the
  // critical strip and the Notes & activity tab's own toggle read the
  // same value. Resolved by recordServiceSide.ts: the viewer's own side,
  // then whatever this viewer last picked for THIS client; a SPICE-only
  // viewer holds the SPICE face and the tab shows her no toggle. Re-read
  // whenever the client or the viewer changes. Computed unconditionally
  // (not just for a 'both' client) so hook order never depends on the
  // record, which hasn't loaded yet here.
  const [serviceSide, setServiceSideState] = useState<RecordServiceSide>(() =>
    resolveRecordServiceSide(whoami, tenantSlug, clientId),
  );
  useEffect(() => {
    setServiceSideState(resolveRecordServiceSide(whoami, tenantSlug, clientId));
  }, [tenantSlug, clientId, whoami]);
  function handleServiceSideChange(next: RecordServiceSide): void {
    writeRecordServiceSide(tenantSlug, clientId, next);
    setServiceSideState(next);
  }

  // --- THE aggregate read (PR-C2): one fetch; every fixture-era tab
  // renders this payload. Gated roles fire no request (enabled=false).
  const { record, isLoading, isError, error, refetch } = useClientRecord(tenantSlug, clientId, {
    enabled: canOpenRecord,
  });

  // A role that holds read_clinical_record but whose reach does not
  // include THIS client (not her service's lane, not her care teams, and
  // not every client under view_all_clinical or the Everyone on staff
  // setting) is refused server-side with 403 not_on_care_team: a
  // different denial than the role gate above, so it renders its own calm
  // state instead of AsyncBoundary's generic error. It wins over a record
  // still cached from before the refusal (a refetch after the setting
  // narrowed); any other failed refetch keeps the cached body.
  const notOnCareTeam =
    isError &&
    error instanceof ApiError &&
    (error.body as { error?: string } | null)?.error === 'not_on_care_team';

  // The record's face: the header's note CTA, the Notes & activity tab
  // and the critical strip's face cell all read this one value. A client
  // on both services resolves through the toggle above (serviceSide); a
  // client on one service has that one face.
  const recordFace: RecordServiceSide | null =
    record === null ? null : recordFaceFor(record.vitals.engagement, serviceSide);
  const headerNoteLabel = recordFace === 'spice' ? '+ Session Note' : '+ Case Note';

  // --- TS-5: archive/restore (soft-archive; server emits the change_log
  // trail + flips clients.is_active/archived_at). Confirm-dialog gated.
  const setArchived = useSetClientArchived(tenantSlug);
  const [confirmArchive, setConfirmArchive] = useState<null | 'archive' | 'restore'>(null);

  // --- WS-4 item 18: pronouns render NEXT TO THE CLIENT'S NAME (in the
  // band they sat after the CM fact and read as the CM's pronouns). The
  // header owns the affordance; the dialog is the same 0128 seam.
  const [pronounsOpen, setPronounsOpen] = useState(false);

  // --- Soak #2 top-bar #1: the client name is editable from the header
  // (view_all_clinical — the founder's admin / exec-support / data-steward,
  // the same client-identity write gate as pronouns/lifecycle). The quiet
  // inline "Rename" affordance opens the ClientNameDialog (display_name-only
  // in v1).
  const [nameOpen, setNameOpen] = useState(false);
  const [changeServiceOpen, setChangeServiceOpen] = useState(false);

  // --- Soak #2 Wave B / G6 (0145): the lifecycle-status editor, opened
  // from the header badge (view_all_clinical roles only). The 0128
  // pronouns dialog seam.
  const [lifecycleOpen, setLifecycleOpen] = useState(false);

  // --- WS-4 D5: the Face Sheet overlay (FaceSheetDialog) — renders the
  // ONE already-fetched snapshot; opening it adds no fetch.
  const [faceSheetOpen, setFaceSheetOpen] = useState(false);
  const [qrfOpen, setQrfOpen] = useState(false);

  // --- 0142 (the humanity upgrade): the client photo. The hero's gold
  // identity circle becomes the face when one is on file; click opens the
  // manage dialog (edit-capable roles — view_all_clinical, the same gate the
  // page + the other identity edits use). photoVersion cache-busts the hero
  // <img> after a replace (the PeopleTab bumpPhotoVersion discipline; the
  // serve response is private/max-age and would otherwise show stale).
  const [photoOpen, setPhotoOpen] = useState(false);
  const [photoVersion, setPhotoVersion] = useState(0);
  const [photoErrored, setPhotoErrored] = useState(false);
  // LP-42: "+ Case Note" asks the Notes tab to bring its note box into view
  // and focus it. Each click bumps the request; the tab hands it back (0)
  // once done, so opening Notes later from the tab bar never scrolls.
  const [composerFocusRequest, setComposerFocusRequest] = useState(0);

  // `today` is computed ONCE at the page boundary and injected — as the
  // OverviewTab prop AND via TodayContext for the deep provenance surfaces
  // (ProvenanceChip / VitalsBand / NotesFeed) — so "today"/"yesterday"
  // phrasing stays deterministic under test; components never read the
  // clock (founder polish 2026-07-06: prose dates read relative for the
  // freshest two days everywhere on the record, not just the Overview).
  // Soak #2 fix: an ANCESTOR TodayContext (tests inject a fixed day) wins
  // over the wall clock, so the whole full-page suite is deterministic and
  // the G1 wall-clock window is not a time bomb; prod has no ancestor
  // provider, so it falls back to the practice's day: the day the record's
  // stamps resolve to (practiceDayOfStamp) and the server's own today.
  const today = useToday() ?? practiceTodayIso();

  const tabVisibility: ClientTabVisibility = {
    // Care record and Life & logistics show to every clinical
    // reader, a SPICE-led viewer included; she reads them with no write
    // door (each door gates on canWriteClinicalRecord).
    hideCareTab: !canReadClinical,
    // Life & logistics mirrors the Care tab's gate (care-record
    // restructure): its four registers ride the same clinical aggregate.
    hideLifeLogisticsTab: !canReadClinical,
    // SPICE note extraction (0189): same read gate as Care record, plus
    // the client's engagement — a pure care_management client has no
    // SPICE facts to show.
    hideSpiceTab: !canReadClinical || record?.vitals.engagement === 'care_management',
    hidePreBillingTab: !canSeePrebilling,
    // People / Documents stay visible in C1 (frame-only; own gates land later).
    hidePeopleTab: false,
    hideDocumentsTab: false,
    // R5: engagement + the SPICE-led viewer flag decide tab ORDER.
    engagement: record?.vitals.engagement,
    spiceLedViewer,
  };

  // Coerce to a visible tab: a role URL-hacking ?tab=pre-billing it can't see
  // gets the first visible tab, never a tab it cannot see.
  const rawTab = resolveClientTab(searchParams.get('tab'));
  const activeTab = isClientTabHidden(rawTab, tabVisibility)
    ? firstVisibleClientTab(tabVisibility)
    : rawTab;

  // S3.3 (launch-UX): the viewer's open-proposal count lights the Notes
  // tab in gold — the ONE gold meaning. SAME query key + fn as
  // ConfirmQueue, so react-query dedupes: opening the tab IS this fetch.
  // A viewer who cannot confirm never fetches (board_observer fires no
  // /proposals request at all; the demo sandbox re-enables it only on a
  // demo client, mirroring the queue's own render gate).
  const canConfirmHere =
    canWriteClinical || (canWriteDemo && (record?.vitals.isDemo ?? false));
  const proposalsQuery = useQuery<ProposalsResponse>({
    queryKey: ['kith-proposals', tenantSlug, clientId],
    queryFn: ({ signal }) => notesApi.listProposals(tenantSlug, clientId, 'open', signal),
    enabled: canOpenRecord && canConfirmHere,
    // Quiet metadata: a failed count NEVER retries or alarms — the badge
    // just stays absent. The queue's own surface owns loud error states.
    retry: false,
  });

  // Pre-billing count stays frame-only. Undefined → no badge (honest).
  const counts: Partial<Record<ClientRecordTab, number>> = {};
  if (proposalsQuery.data !== undefined) {
    counts.notes = proposalsQuery.data.proposals.length;
  }

  function handleTabChange(next: ClientRecordTab): void {
    const sp = new URLSearchParams(searchParams);
    sp.set('tab', next);
    setSearchParams(sp, { replace: true });
  }

  function goToNotes(): void {
    handleTabChange('notes');
    setComposerFocusRequest((n) => n + 1);
  }

  const deniedContent = (
    <>
      <div
        role="alert"
        className="my-3 p-3 border border-warm-300 bg-warm-50 rounded-card text-sm text-ink"
      >
        <RecordDeniedLine />
      </div>
      <BackControl
        label="Home"
        ariaLabel="Back to home"
        to={`/t/${encodeURIComponent(tenantSlug)}/hub`}
        testId="back-to-home"
        className="mt-4"
      />
    </>
  );

  return (
    <TodayContext.Provider value={today}>
    <PageShell
      maxWidth="wide"
      testId="client-record-page"
      access={{ allowed: canOpenRecord, deniedContent }}
    >
      {notOnCareTeam ? (
        // Refused by the server's care-team gate, not the role gate
        // above: this viewer CAN open records, just not this one. Decided
        // before the record branch, so the refusal replaces a record
        // still cached from an earlier read.
        <>
          <div
            role="alert"
            className="my-3 p-3 border border-warm-300 bg-warm-50 rounded-card text-sm text-ink"
            data-testid="not-on-care-team"
          >
            This client is not on your care team. Ask the office if you
            need to be added.
          </div>
          {/* This viewer opens client records, so the roster is the way back. */}
          <div className="mt-4">
            <BackToClients tenantSlug={tenantSlug} />
          </div>
        </>
      ) : record === null ? (
        // Loading / error for the ONE aggregate read (house AsyncBoundary).
        <AsyncBoundary
          isLoading={isLoading}
          isError={isError}
          error={error}
          onRetry={refetch}
          emptyHeadline="This client record can't be opened. Go back to Clients and pick the client again."
        >
          <></>
        </AsyncBoundary>
      ) : (
        <>
          <BackToClients tenantSlug={tenantSlug} />
          {/* THE HERO GROUP (Soak #2 seam, round 2): the forest band and
              the sage apron below MUST live inside ONE wrapper. They used
              to be siblings directly under PageShell's <main>, whose
              space-y-6 injected a 24px margin BETWEEN them — the cream
              page background showed through as the founder's "beige
              strip". Wave A's pt-0 fixed the sage strip INSIDE the apron
              but not this inter-sibling gap; the wrapper takes the
              space-y margin as one unit so band and card meet flush. */}
          <div data-testid="hero-group">
          {/* The client band. FOREST — the house band ground (founder
              ruling 2026-07-10, UX sweep: "the purple banner doesn't
              belong"; this retires the 07-06 heather band). The band
              takes the codified DL v1 band anatomy — forest ground, gold
              eyebrow, cream serif name, dove watermark in cream — so a
              client's record opens on the same personality register as
              the rest of the app. Heather stays what the tokens reserve
              it for: the Memory Care chip family, never a banner. The
              gold identity circle is the DL5 gold-circle motif carrying
              the client's initials. */}
          <div
            className="relative rounded-t-card bg-sage-800 px-4 py-5 sm:px-6"
            data-testid="client-context-band"
          >
            {/* Design Pass 3 (4c, THE kebab bug): the band itself must NOT
                be overflow-hidden — it clipped the overflow menu to an
                empty white sliver. The watermark bleed gets its own
                clipped layer instead; dropdowns escape the band freely. */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 overflow-hidden rounded-t-card"
            >
              <BrandWatermark
                fit="height"
                className="inset-y-0 right-0 translate-x-[35%] text-cream/[6%]"
              />
            </div>
            {/* A5: the hero's right side works now — next appointment +
                billing state sit where dead space was. S3.1: the row WRAPS
                below md so those same facts drop to a compact full-width
                row under the header instead of vanishing. */}
            <div className="relative flex flex-wrap items-start justify-between gap-6 gap-y-3 md:flex-nowrap">
              <div className="min-w-0 flex-1">
          <EntityHeader
            eyebrow="Client"
            eyebrowClassName="text-warm-400"
            nameClassName="text-cream"
            identity={(() => {
              // 0142 (the humanity upgrade): the DL5 gold identity circle
              // becomes the client's face when a photo is on file (lazily
              // loaded; initials on none / load error). Edit-capable roles
              // (view_all_clinical — the page + identity-edit gate) click it
              // to manage the photo; read-only roles get the plain circle.
              const showPhoto = record.vitals.hasPhoto && !photoErrored;
              const inner = showPhoto ? (
                <img
                  src={clientPhotoUrl(tenantSlug, clientId, photoVersion || undefined)}
                  alt={`Photo of ${record.vitals.displayName}`}
                  data-testid="hero-photo-img"
                  className="h-[3.625rem] w-[3.625rem] shrink-0 rounded-full object-cover bg-warm-400"
                  onError={() => setPhotoErrored(true)}
                />
              ) : (
                <span
                  data-testid="hero-photo-initials"
                  className="flex h-[3.625rem] w-[3.625rem] shrink-0 items-center justify-center rounded-full bg-warm-400 font-display text-[1.3125rem] font-semibold text-sage-900"
                >
                  {initials(record.vitals.displayName) || '?'}
                </span>
              );
              // The photo is an everyday client detail: a seat that edits
              // them gets the clickable "Change photo" affordance; everyone
              // else sees the image or initials.
              if (!canEditDetails) return inner;
              return (
                <button
                  type="button"
                  data-testid="hero-photo-manage"
                  onClick={() => setPhotoOpen(true)}
                  title={record.vitals.hasPhoto ? 'Change photo' : 'Add photo'}
                  aria-label={record.vitals.hasPhoto ? 'Change client photo' : 'Add client photo'}
                  className="shrink-0 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-warm-400 focus-visible:ring-offset-2 focus-visible:ring-offset-sage-800 hover:opacity-90"
                >
                  {inner}
                </button>
              );
            })()}
            name={record.vitals.displayName}
            nameSlot={
              // UI polish pass (PART 1): "Test Client (he/him) [Active]
              // [Care management]" — pronouns render as a literal
              // parenthetical right after the name (still the click-to-edit
              // door, WS-4 item 18's 0128 seam), then the status pill and
              // the engagement pill side by side. Change status / Rename
              // moved OFF the band into the kebab (HeroOverflowMenu, below).
              <>
                <span className="truncate" title={record.vitals.displayName}>
                  {record.vitals.displayName}
                </span>
                {/* The name the client goes by, beside the legal name
                    ("Charles Testwell (Chuck)"). The span sets no size
                    or family of its own, so it inherits the h1's
                    font-display text-2xl, and carries only the ink
                    (text-cream/90) plus the overflow guard (min-w-0
                    [overflow-wrap:anywhere], so a long value wraps inside
                    the band at a 195px layout instead of pushing it wide).
                    Nothing renders when blank (founder: "if blank, don't
                    show"). Guard + render on the TRIMMED value
                    (belt-and-suspenders over the single-writer trim) so a
                    whitespace-only value never renders an empty "( )". */}
                {record.vitals.preferredName?.trim() && (
                  <span
                    className="min-w-0 [overflow-wrap:anywhere] text-cream/90"
                    data-testid="client-preferred-name"
                  >
                    ({record.vitals.preferredName.trim()})
                  </span>
                )}
                {/* PART 1.1: pronouns as a literal parenthetical — cream
                    type, dimmer + smaller than the name, immediately after
                    it. Still the click-to-edit affordance (opens the same
                    0128 PronounsDialog below); an unrecorded value keeps
                    the "+ pronouns" invitation. */}
                {/* Pronouns are an everyday client detail. A seat that edits
                    them gets the click-to-edit button; a read-only viewer sees
                    the value as plain text (and nothing at all when
                    unrecorded: no "+ pronouns" invite). */}
                {canEditDetails ? (
                  <button
                    type="button"
                    data-testid="header-pronouns"
                    title={record.vitals.pronouns ? 'Change pronouns' : 'Record pronouns'}
                    aria-label={record.vitals.pronouns ? 'Change pronouns' : 'Record pronouns'}
                    className="font-sans text-sm font-normal text-cream/60 hover:text-cream/85 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-warm-400 rounded-tight"
                    onClick={() => setPronounsOpen(true)}
                  >
                    {record.vitals.pronouns ? `(${record.vitals.pronouns})` : '+ pronouns'}
                  </button>
                ) : (
                  record.vitals.pronouns && (
                    <span
                      data-testid="header-pronouns-readonly"
                      className="font-sans text-sm font-normal text-cream/60"
                    >
                      ({record.vitals.pronouns})
                    </span>
                  )
                )}
                {/* The status chip: the real-world status, then its note in
                    brackets ("Inactive (Awaiting payment)"), register-safe
                    via lifecycleBadge. Archive OVERRIDES to a neutral
                    "Archived" when soft-archived (orthogonal axes), whatever
                    the note. Death is a QUIET neutral marker, never an
                    alarm. The chip IS the control (the pronouns seam): a
                    manage_client_identity holder on an active client clicks
                    it straight into Change client status; an archived
                    client's status is read-only here too (change it via
                    Restore first, same gate the kebab used). The button is
                    named with what the chip shows, so a screen reader hears
                    the status and its note. */}
                {(() => {
                  const badge = lifecycleBadge(
                    record.vitals.lifecycleStatus,
                    record.vitals.isActive,
                    record.vitals.statusNote,
                  );
                  const chip = (
                    <Chip
                      variant={badge.variant}
                      className="font-sans text-label font-medium"
                      data-testid="client-status-chip"
                    >
                      {badge.label}
                    </Chip>
                  );
                  // Every chip on the band sits in the same slot, the
                  // control or not, so a seat that cannot change the
                  // status sees the same line as one that can.
                  if (!canManageIdentity || !record.vitals.isActive) {
                    return (
                      <span data-testid="header-status-wrap" className={HEADER_CHIP_SLOT}>
                        {chip}
                      </span>
                    );
                  }
                  return (
                    <button
                      type="button"
                      data-testid="header-lifecycle-edit"
                      title="Change client status"
                      aria-label={`Change client status: ${badge.label}`}
                      className={`${HEADER_CHIP_SLOT} rounded-tight focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-warm-400`}
                      onClick={() => setLifecycleOpen(true)}
                    >
                      {chip}
                    </button>
                  );
                })()}
                {/* On hospice (0243): the day the client went on hospice,
                    beside the status, in the gold the weekly call board
                    gives it. Set and cleared in Change client status, so
                    for the seats that change the status it opens that
                    dialog, like the status chip. LP-25: not on a client
                    whose status says they died, as on the weekly call
                    board; that dialog then shows Date of death, not the
                    hospice day. */}
                {record.vitals.onHospiceSince !== null &&
                  record.vitals.lifecycleStatus !== 'deceased' &&
                  record.vitals.lifecycleStatus !== 'deceased_family_work' && (() => {
                  const label = `On hospice (${rowDate(record.vitals.onHospiceSince, true)})`;
                  const chip = (
                    <Chip variant="specialty" className="font-sans text-label font-medium" data-testid="client-hospice-chip">
                      {label}
                    </Chip>
                  );
                  if (!canManageIdentity || !record.vitals.isActive) {
                    return (
                      <span data-testid="header-hospice-wrap" className={HEADER_CHIP_SLOT}>
                        {chip}
                      </span>
                    );
                  }
                  return (
                    <button
                      type="button"
                      data-testid="header-hospice-edit"
                      title="Change client status"
                      aria-label={`Change client status: ${label}`}
                      className={`${HEADER_CHIP_SLOT} rounded-tight focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-warm-400`}
                      onClick={() => setLifecycleOpen(true)}
                    >
                      {chip}
                    </button>
                  );
                })()}
                {/* The engagement pill, beside the status pill in the same slot
                    (HEADER_CHIP_SLOT), so both land on one line. */}
                {/* The SPICE accent role: a SPICE-only client's engagement
                    chip reads "SPICE" and takes the pepper tint, the same
                    treatment a session note's chip carries in the feed. A
                    client on both services names both lines and stays
                    neutral — it is not itself a SPICE face. */}
                {/* The chip never runs past its row: the wrapper may shrink
                    to the row, and the chip wraps its label inside it. */}
                <span data-testid="header-engagement-wrap" className={HEADER_CHIP_SLOT}>
                  <Chip
                    variant={record.vitals.engagement === 'spice' ? 'spice' : 'neutral'}
                    className="max-w-full break-words font-sans text-label font-medium"
                    data-testid="header-engagement"
                  >
                    {ENGAGEMENT_LABELS[record.vitals.engagement]}
                  </Chip>
                </span>
              </>
            }
            // DP4 §C.2 (hero/Reach de-dup): the "Home · Currently at" meta
            // line retired from the hero — the Reach band below already
            // owns both facts (Home address, Currently at), and the
            // hero's job is next-appointment + billing (HeroFacts), not
            // contact facts. WS-4 D2's "where is the client right now"
            // need is still met, just from one canonical place.
            actions={
              <>
                {/* Founder ruling 2026-07-06: "+ Case Note" is THE uniform
                    primary treatment for this action everywhere — on the
                    forest band that means the on-dark rung (cream fill),
                    exactly the ClientsHome band's treatment. Hidden for a
                    read-only viewer (0152) — a write-intent CTA. A
                    SPICE-only client speaks session vocabulary throughout
                    the record (the composer this button opens already
                    heads itself "Post a session note" here), so the label
                    follows the record's current face: "+ Session Note" on
                    the SPICE face (a spice client, or a both client with
                    the toggle on SPICE); every other face keeps
                    "+ Case Note". */}
                {(canWriteClinical || (canWriteDemo && record.vitals.isDemo)) && (
                  <Button
                    variant="primary-on-dark"
                    size="sm"
                    onClick={goToNotes}
                    data-testid="new-note-action"
                  >
                    {headerNoteLabel}
                  </Button>
                )}
                {/* TS-5 lifecycle, moved to the quiet overflow (A5):
                    destructive actions never sit beside the primary. PART
                    1.3 folded Rename client in too ("Change status" lives
                    on the header badge instead). */}
                <HeroOverflowMenu
                  isActive={record.vitals.isActive}
                  canEdit={canManageIdentity}
                  canEditDetails={canEditDetails}
                  onArchive={() => setConfirmArchive('archive')}
                  onRestore={() => setConfirmArchive('restore')}
                  onFaceSheet={() => setFaceSheetOpen(true)}
                  onQrf={() => setQrfOpen(true)}
                  onChangeService={() => setChangeServiceOpen(true)}
                  onRename={() => setNameOpen(true)}
                />
              </>
            }
          />
              </div>
              <HeroFacts record={record} today={today} canSeePrebilling={canSeePrebilling} />
            </div>
          </div>

          {/* Design Pass 3 (4a) + Soak #2 #7: the sage apron frames the
              vitals card on the sides and below, but its TOP strip is
              collapsed (pt-0) so the white card meets the forest band
              directly — the founder's "horizontal disconnect" was that
              single-spaced sage strip between the band and the card. */}
          {/* CONNECTED (founder ruled 2026-08-25): the band IS this card's
              header — one silhouette, one shared edge. The sage apron is
              RETIRED; its half-inset left the white card "floating just
              off" the band, which read as a mistake because it was one:
              the leftover half of the ruling that collapsed the top gap. */}
            {/* THE ONE VITALS BAND: row one is the action-gating strip
                (care team, emergency contact, code status, then transport
                consent on the care management face or conditions on the
                SPICE face, allergies last); below it the About band
                (identity facts) and the Reach band (contact facts). */}
            <div
              data-testid="client-vitals"
              className="rounded-b-card bg-paper border border-t-0 border-sage-800/[8%]"
            >
              <CriticalStrip record={record} face={recordFaceFor(record.vitals.engagement, serviceSide)} />
              <VitalsBand vitals={record.vitals} addresses={record.addresses} />
            </div>
          </div>

          <div className="mt-2">
            <ClientRecordTabs
              activeTab={activeTab}
              counts={counts}
              onChange={handleTabChange}
              {...tabVisibility}
            />
          </div>

          {/* Care-record restructure: the at-a-glance jump strip sticks
              just under the tab bar on the Care record ONLY — quiet sage
              wayfinding pills ("Medications 3"), each emitting a section
              hash the G4 machinery consumes (scroll + force-open). */}
          {activeTab === 'care' && <RecordJumpStrip record={record} />}

          <div id="client-tabpanel" role="tabpanel" className="mt-4">
            {/* S4: the record lands here — Overview retired (founder-ruled
                2026-08-23); opening a client means standing at the composer
                with the feed and queue at hand. `today` still rides
                TodayContext for the deep provenance surfaces. */}
            {/* Notes renders like every other tab: reaching here means the
                page gate (read_clinical_record) already granted clinical
                access. The old add_notes gate wrongly hid the notes surface
                from a role that holds the Kith clinical caps but not the
                legacy add_notes. */}
            {activeTab === 'notes' && (
              <NotesActivityTab
                clientId={clientId}
                clientIsDemo={record.vitals.isDemo}
                engagement={record.vitals.engagement}
                serviceSide={serviceSide}
                onServiceSideChange={handleServiceSideChange}
                viewerIsCareManager={record.vitals.viewerIsCareManager}
                clientName={clientCallName(record.vitals)}
                composerFocusRequest={composerFocusRequest}
                onComposerFocused={() => setComposerFocusRequest(0)}
              />
            )}
            {activeTab === 'care' && <CareRecordTab record={record} />}
            {/* Life & logistics (care-record restructure): the four
                practical registers off the SAME aggregate snapshot —
                no new fetch, same clinical gate as the Care record. */}
            {activeTab === 'life-logistics' && <LifeLogisticsTab record={record} />}
            {/* SPICE note extraction (0189): the session list, off the
                same aggregate snapshot for the schedule summary, plus its
                own live fetch for the confirmed sessions. */}
            {activeTab === 'spice' && <SpiceTab record={record} />}
            {activeTab === 'people' && <PeopleTab record={record} />}
            {/* Documents is LIVE (D8 v1, 0131) — its own list fetch +
                the SAS-handshake upload (documentsApi), not the
                aggregate snapshot's shape. */}
            {activeTab === 'documents' && (
              <DocumentsTab clientId={clientId} engagement={record.vitals.engagement} />
            )}
            {/* Pre-billing is LIVE (native billing v0) — its own month-
                scoped fetch (view_invoice population; not the aggregate
                snapshot's shape). `today` + the client's display name are
                injected from the page boundary. */}
            {activeTab === 'pre-billing' && (
              <PreBillingTab
                clientId={clientId}
                clientName={record.vitals.displayName}
                today={today}
                engagement={record.vitals.engagement}
                // ?month= deep-link (invoice workflow pass): an invoice
                // draft's edit-at-source link lands on the invoice's
                // month. PreBilling validates + clamps; absent = current.
                initialMonth={searchParams.get('month') ?? undefined}
              />
            )}
          </div>

          {/* WS-4 D5: the Face Sheet — the Emergency Folder one-pager,
              rendered from the SAME snapshot (no extra read). */}
          {faceSheetOpen && (
            <FaceSheetDialog
              record={record}
              today={today}
              onClose={() => setFaceSheetOpen(false)}
              tenantSlug={tenantSlug}
              photoVersion={photoVersion || undefined}
            />
          )}
          {qrfOpen && (
            <QrfDialog record={record} today={today} tenantSlug={tenantSlug} onClose={() => setQrfOpen(false)} />
          )}

          {/* WS-4 item 18: the header's pronouns affordance (the 0128
              dialog, unchanged seam — it moved home, not behavior). */}
          {pronounsOpen && (
            <PronounsDialog
              currentPronouns={record.vitals.pronouns ?? ''}
              onClose={() => setPronounsOpen(false)}
            />
          )}

          {/* The menu's Change service: the service and its leads, one
              save. The About row's own Edit mounts its own dialog. */}
          {changeServiceOpen && (
            <ChangeServiceDialog vitals={record.vitals} onClose={() => setChangeServiceOpen(false)} />
          )}
          {/* The menu's Edit name: the structured-name write path. */}
          {nameOpen && (
            <ClientNameDialog
              currentDisplayName={record.vitals.displayName}
              currentFirstName={record.vitals.firstName}
              currentLastName={record.vitals.lastName}
              currentPreferredName={record.vitals.preferredName}
              onClose={() => setNameOpen(false)}
            />
          )}

          {/* Change client status: the status cards, the status note and
              the one date the status calls for, opened from the header chip. Orthogonal to archive (setting
              'deceased' never archives). */}
          {lifecycleOpen && (
            <LifecycleStatusDialog
              currentStatus={record.vitals.lifecycleStatus}
              currentNote={record.vitals.statusNote}
              currentHospiceSince={record.vitals.onHospiceSince}
              currentDateOfDeath={record.vitals.dateOfDeath}
              currentDob={record.vitals.dob}
              onClose={() => setLifecycleOpen(false)}
            />
          )}

          {/* 0142 (the humanity upgrade): the client photo manage dialog,
              opened from the hero's identity circle (edit-capable roles). */}
          {photoOpen && (
            <ClientPhotoDialog
              clientId={clientId}
              displayName={record.vitals.displayName}
              hasPhoto={record.vitals.hasPhoto}
              onClose={() => setPhotoOpen(false)}
              onChanged={() => {
                setPhotoErrored(false);
                setPhotoVersion(Date.now());
              }}
            />
          )}

          {/* The confirm before the lifecycle flip (alertdialog role;
              conditional-render pattern). */}
          {confirmArchive !== null && (
            <ArchiveConfirmDialog
              action={confirmArchive}
              displayName={record.vitals.displayName}
              pronouns={record.vitals.pronouns}
              pending={setArchived.isPending}
              error={setArchived.isError ? setArchived.error : null}
              onConfirm={() =>
                setArchived.mutate(
                  { id: clientId, archived: confirmArchive === 'archive' },
                  { onSuccess: () => setConfirmArchive(null) },
                )
              }
              onClose={() => setConfirmArchive(null)}
            />
          )}
        </>
      )}
    </PageShell>
    </TodayContext.Provider>
  );
}
